import { ref } from 'vue';
import { Preferences } from '@capacitor/preferences';

export function useStorage() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const setItem = async <T>(key: string, value: T): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const serialized = JSON.stringify(value);
      await Preferences.set({ key, value: serialized });
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to set item';
      console.error('Storage set error:', e);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const getItem = async <T>(key: string): Promise<T | null> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { value } = await Preferences.get({ key });

      if (value === null) {
        return null;
      }

      return JSON.parse(value) as T;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to get item';
      console.error('Storage get error:', e);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const removeItem = async (key: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await Preferences.remove({ key });
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to remove item';
      console.error('Storage remove error:', e);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const clear = async (): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await Preferences.clear();
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to clear storage';
      console.error('Storage clear error:', e);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const keys = async (): Promise<string[]> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { keys: storedKeys } = await Preferences.keys();
      return storedKeys;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to get keys';
      console.error('Storage keys error:', e);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    setItem,
    getItem,
    removeItem,
    clear,
    keys,
  };
}
